

import React from "react";
import { BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { REFERRAL_SOURCE_OPTIONS } from '@/utils/referralSource';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ADMIN_LANDING_URL } from "@/constants/navigation";
import { visibleAdminCategories } from "@/constants/adminNavCategories";
import AdminNavCategories from "@/components/nav/AdminNavCategories";
import AdminNavMegaMenu from "@/components/nav/AdminNavMegaMenu";
import { describeResetError, RESET_SUPPORT_LINE } from "@/lib/authErrors";
import { isNativeApp } from "@/native/bootstrap";
import { isOffline, isNetworkAuthError, offlineAccount } from "@/native/offline";
import { useHideNav } from "@/context/HideNavContext";
import { Plane, Upload, Map, Menu, User, LogOut, Home, UserCircle, BarChart, Users, Search, ChevronDown, CreditCard, TrendingUp, MessageCircle, UserCog, Activity, HelpCircle, Download, Award, ArrowLeft, Receipt, AlertTriangle, BookOpen, ShieldAlert, Shield, Wallet, Globe, Gauge, FlaskConical, Clock, Briefcase, Sparkles, Inbox, Scale } from "lucide-react";
import { initGlobalErrorHandlers, setErrorLoggerUser } from "@/lib/errorLogger.jsx";
import { useReserveEligibility } from "@/hooks/useReserveEligibility";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { User as UserEntity } from "@/api/entities";
import { motion } from "framer-motion";
import { toast } from 'sonner';
import MetaPixel from "@/components/MetaPixel";
import { trackSignupConversion } from "@/lib/chatgptAdsPixel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MyVipBadge from '@/components/vip/MyVipBadge';
import { useMyVip } from '@/hooks/useMyVip';

const navigationItems = [
  {
    title: "Home",
    url: createPageUrl("Home"),
    icon: Home
  },
  {
    title: "Book",
    url: createPageUrl("search"),
    icon: Plane
  },
  {
    title: "Trips",
    url: createPageUrl("Dashboard"),
    icon: Map
  },
  {
    // Reserve was discontinued for new users. Existing customers with a
    // wallet balance still see this entry — see `requireReserveEligibility`
    // gate in the desktop + mobile nav blocks below.
    title: "Reserve",
    url: "/app/reserve",
    icon: Wallet,
    requireReserveEligibility: true,
  },
  {
    title: "Rewards",
    url: "/rewards",
    icon: Award
  },
  {
    // Admin-only audit feed for booking activity (NMI installments,
    // manual payments, flight changes, cancellations, schedule edits,
    // card updates, credits). NOT customer-facing — RLS on
    // customer_action_log restricts reads to admins, the Edge fn
    // returns 403 for non-admin tokens (see migration 079), and the
    // mobile sheet + nav loops below honor `adminOnly: true`.
    title: "Activity",
    url: "/activity",
    icon: Activity,
    adminOnly: true,
  },
  {
    title: "Carts",
    url: createPageUrl("Carts"),
    icon: CreditCard,
    adminOnly: true,
  },
  {
    title: "Analytics",
    url: createPageUrl("Analytics"),
    icon: BarChart,
    adminOrInvestor: true,
  },
  {
    title: "Users",
    url: createPageUrl("Users"),
    icon: Users,
    adminOnly: true,
  },
  {
    title: "Accounting",
    url: createPageUrl("Accounting"),
    icon: Receipt,
    adminOnly: true,
  },
  {
    // Deposits landing vs airfare still to buy. Sits beside Accounting on
    // purpose: it is the question people go to Accounting looking for, and
    // it used to live only inside Bernard's daily email.
    //
    // Admin-only in three places, not one: this flag keeps the tab out of
    // customer nav, the page itself gates on the admin role, and
    // cash_ladder_snapshots has RLS on with zero policies so the figures
    // (which include the company's cash position) are reachable only by the
    // service role through the requireAdmin-gated routes.
    title: "Cash Ladder",
    url: "/cash-ladder",
    icon: Scale,
    adminOnly: true,
    // Lives in the Workspace menu (Money), beside Accounting, on desktop and
    // mobile alike (src/constants/adminNavCategories.js).
  },
  {
    // The admin working surface: the concierge inquiry queue, which is also
    // where admins land on sign-in (see ADMIN_LANDING_URL below). Named for
    // the destination rather than the department — this slot used to read
    // "Staff" and open the AI staff roster, which buried the one page admins
    // open all day one click deeper.
    //
    // The roster (org chart, daily reports, DM chat with the AI staff) is not
    // orphaned: the inquiries desk header links back to /staff, which is the
    // direction admins actually travel. Server routes are requireAdmin-gated;
    // this flag just keeps the tab out of customer nav.
    title: "Support",
    url: ADMIN_LANDING_URL,
    icon: Inbox,
    adminOnly: true,
    // ...but also reachable by `concierge`, the desk-only role held by the
    // browser agent. This is the ONLY admin nav entry it sees: the queue it
    // works. Trips is not adminOnly, so it shows for everyone already, and
    // the concierge session reads the whole book there (read-only — edit
    // controls gate on `admin`, and update-booking rejects the role).
    conciergeOk: true,
  },
  {
    title: "Error Log",
    url: createPageUrl("ErrorLog"),
    icon: AlertTriangle,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Black List",
    url: createPageUrl("BlackList"),
    icon: ShieldAlert,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Past Due",
    url: createPageUrl("Past Due"),
    icon: Clock,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Collections",
    url: createPageUrl("Collections"),
    icon: Briefcase,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Risk Insights",
    url: createPageUrl("RiskInsights"),
    icon: Shield,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Risk Review",
    url: createPageUrl("RiskReview"),
    icon: ShieldAlert,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Portfolio Intelligence",
    url: createPageUrl("PortfolioIntelligence"),
    icon: Gauge,
    adminOnly: true,
    isBottomItem: true,
  },
  {
    title: "Model Performance",
    url: "/model-performance",
    icon: FlaskConical,
    adminOnly: true,
    isBottomItem: true,
  }
];

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hideNav } = useHideNav();
  const { isEligible: isReserveEligible } = useReserveEligibility();
  // Ask once up front, so the VIP badge is ready when the account menu opens.
  useMyVip();
  
  // Initialize currentUser synchronously from localStorage to prevent white screen flash
  const [currentUser, setCurrentUser] = React.useState(() => {
    try {
      const storedUser = localStorage.getItem('defera.user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        
        // ONE-TIME FIX: Clear cache for admin users with wrong role
        if (userData.email === 'jayjay.gonzalez@icloud.com' && userData.role === 'user') {
          console.log('🔧 Clearing incorrect cached role for admin user');
          localStorage.removeItem('defera.user');
          return null;
        }
        
        // Version 3+ required for secure admin role detection (app_metadata.roles)
        if (userData.version >= 3 && userData.email && userData.id) {
          return userData;
        } else if (storedUser) {
          // Clear outdated cache
          localStorage.removeItem('defera.user');
        }
      }
    } catch (e) {
      console.error('Error parsing stored user:', e);
    }
    return null;
  });
  const [isLoadingUser, setIsLoadingUser] = React.useState(true);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [referralSource, setReferralSource] = React.useState('');
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [isSignup, setIsSignup] = React.useState(true);
  const [authError, setAuthError] = React.useState('');
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState('');
  const [resetEmailSent, setResetEmailSent] = React.useState(false);
  const [resetSending, setResetSending] = React.useState(false);
  const [resetError, setResetError] = React.useState(null); // { title, detail } from describeResetError, or null
  const isRedirectingRef = React.useRef(false);
  const currentUserRef = React.useRef(currentUser);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showInstallButton, setShowInstallButton] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);

  // Keep ref in sync with state
  React.useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Initialize global error handlers on mount
  React.useEffect(() => {
    initGlobalErrorHandlers();
  }, []);

  // Sync current user with error logger
  React.useEffect(() => {
    setErrorLoggerUser(currentUser);
  }, [currentUser]);

  // Handle auth_mode query parameter from AuthGate redirect
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authMode = searchParams.get('auth_mode');
    if (authMode === 'login') {
      setIsSignup(false);
    } else if (authMode === 'signup') {
      setIsSignup(true);
    } else if (authMode === 'forgot') {
      // Deep link straight to the reset form. AuthCallback sends a customer
      // here when their reset link was already spent, so landing them on a
      // login form they can't complete would just restart the loop
      // (Margo Dixon, 2026-09-17).
      setIsSignup(false);
      setShowForgotPassword(true);
    }
  }, [location.search]);

  // PWA Install Prompt Handler
  React.useEffect(() => {
    // Detect iOS devices (including modern iPadOS which reports as "Macintosh")
    const isIOSDevice = (
      (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) ||
      // iPadOS 13+ reports as "Macintosh" with touch support
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
    setIsIOS(isIOSDevice);
    
    // Show the install button on every platform for consistency, except
    // inside the iOS app, which is already installed: there "Add to Home
    // Screen" instructions make no sense.
    setShowInstallButton(!isNativeApp);
    
    // For Chrome/Android - use beforeinstallprompt for native installation
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      console.log('PWA install prompt available - native installation enabled');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // Detect Safari browser (both mobile and desktop)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // iOS/iPadOS-specific instructions
    if (isIOS) {
      toast.info(
        'To install: Tap the Share button (square with arrow) at the bottom of Safari, then tap "Add to Home Screen"',
        { duration: 8000 }
      );
      return;
    }
    
    // Safari on desktop - also show Add to Home Screen instructions
    if (isSafari) {
      toast.info(
        'To install on Safari: Tap the Share button in Safari, then select "Add to Home Screen" to install Defera as an app',
        { duration: 8000 }
      );
      return;
    }
    
    // Chrome/Edge/Android with native install prompt
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        toast.success('App installed successfully! 🎉');
        setDeferredPrompt(null);
      } else {
        toast.info('Installation cancelled');
      }
      return;
    }
    
    // Browsers without native PWA support (Firefox, etc.)
    toast.info(
      'For best experience, use Chrome or Edge to install Defera as an app. Your bookmarks work great too!',
      { duration: 6000 }
    );
  };

  React.useEffect(() => {
    let authSubscription = null;
    let sessionCheckInterval = null;
    let visibilityHandler = null;
    
    const clearSessionAndRedirect = () => {
      // Prevent multiple redirects
      if (isRedirectingRef.current) return;
      isRedirectingRef.current = true;
      
      console.log('🔴 Session ended - clearing all data and redirecting to login');
      
      // Unsubscribe from auth changes immediately to stop the loop
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      
      // Clear periodic check
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
      
      // Clear all data and redirect
      setCurrentUser(null);
      localStorage.clear();
      navigate(createPageUrl('Home'));
    };
    
    // INDUSTRY STANDARD: Validate session is still active
    const validateSession = async () => {
      // Offline, an expired sign-in can't be checked (Supabase would spend
      // ~30s retrying first). Check again once there's a connection.
      if (isOffline()) return true;
      try {
        const { supabase } = await import('../lib/supabase');
        const { data: { session }, error } = await supabase.auth.getSession();

        // If we have a user but no session, log them out
        if (currentUserRef.current && !session) {
          // Supabase couldn't be reached to refresh the sign-in: that's the
          // network, not a sign-out, and signing out here would wipe the
          // sign-in and everything saved on the device. Supabase refreshes it
          // once back online, or sends SIGNED_OUT if it has really ended.
          if (isNetworkAuthError(error)) return true;
          console.warn('⚠️ Session expired - logging out user');
          clearSessionAndRedirect();
          return false;
        }
        
        // If session exists, validate it's not expired
        if (session) {
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const now = Date.now();
          
          if (expiresAt > 0 && now >= expiresAt) {
            console.warn('⚠️ Session token expired - logging out user');
            clearSessionAndRedirect();
            return false;
          }
        }
        
        return !!session;
      } catch (error) {
        console.error('Session validation error:', error);
        // On validation error with logged-in user, log them out for safety
        // (unless the connection dropped mid-check — see above)
        if (currentUserRef.current && !isOffline()) {
          console.warn('⚠️ Cannot validate session - logging out for security');
          clearSessionAndRedirect();
        }
        return false;
      }
    };
    
    const loadUser = async () => {
      try {
        // Offline in the iOS app: open straight into the account signed in
        // on this phone instead of waiting ~30s for Supabase to fail to
        // refresh an expired sign-in.
        if (isNativeApp && isOffline()) {
          const account = offlineAccount();
          if (account) {
            setCurrentUser(account);
            return;
          }
        }

        // Import Supabase
        const { supabase } = await import('../lib/supabase');

        // Check if Supabase has a session (it manages its own persistence now)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (!session) {
          // Supabase couldn't be reached to refresh an expired sign-in: stay
          // signed in as the account saved on this device (see validateSession).
          const account = isNetworkAuthError(sessionError) ? offlineAccount() : null;
          if (account) {
            setCurrentUser(account);
            return;
          }
          // No session - clear any cached user data and wait for login
          setCurrentUser(null);
          localStorage.removeItem('defera.user');
          setIsLoadingUser(false);
          return;
        }
        
        console.log('✅ Supabase session found, loading user...');
        
        // Check localStorage for cached user data first (faster than DB query)
        const storedUser = localStorage.getItem('defera.user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            // Verify cached data is complete and valid
            // Version 4+ required for investor role support
            // CRITICAL: Also verify that cached role matches session app_metadata roles
            const sessionRoles = session.user.app_metadata?.roles || [];
            const sessionIsBacker = sessionRoles.includes('backer');
            const cachedIsBacker = userData.app_metadata?.roles?.includes('backer') || userData.role === 'backer';
            
            // If session has backer role but cache doesn't, refresh from session
            if (sessionIsBacker && !cachedIsBacker) {
              console.log('🔄 Session has backer role but cache does not - refreshing user data');
              localStorage.removeItem('defera.user');
            } else if (userData.version >= 4 && 
                userData.firstName && 
                userData.lastName && 
                userData.role &&
                userData.email &&
                userData.id &&
                userData.app_metadata) {
              // Update app_metadata from session to ensure it's current
              // Also recalculate role from session app_metadata to ensure accuracy
              const sessionRoles = session.user.app_metadata?.roles || [];
              const sessionIsAdmin = sessionRoles.includes('admin');
              const sessionIsInvestor = sessionRoles.includes('investor');
              const sessionIsBacker = sessionRoles.includes('backer');
              
              let correctRole = 'user';
              if (sessionIsAdmin) {
                correctRole = 'admin';
              } else if (sessionIsInvestor) {
                correctRole = 'investor';
              } else if (sessionIsBacker) {
                correctRole = 'backer';
              }
              
              const updatedUserData = {
                ...userData,
                role: correctRole, // Use role calculated from session app_metadata
                app_metadata: session.user.app_metadata || userData.app_metadata
              };
              
              // Update cache with correct role
              localStorage.setItem('defera.user', JSON.stringify(updatedUserData));
              
              console.log('Found valid cached user data:', updatedUserData.email, 'Role:', updatedUserData.role, 'Roles:', updatedUserData.app_metadata?.roles);
              setCurrentUser(updatedUserData);
              setIsLoadingUser(false);
              return;
            } else {
              console.log('Outdated or incomplete cached user data - will reload from database');
              localStorage.removeItem('defera.user');
            }
          } catch (e) {
            console.error('Error parsing stored user data:', e);
            localStorage.removeItem('defera.user');
          }
        }
        
        // INDUSTRY STANDARD: Use session metadata as source of truth
        console.log('📋 Loading user profile from session metadata...');
        
        // SECURITY FIX: Check app_metadata.roles (secure) instead of user_metadata.role (insecure)
        const roles = session.user.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin');
        const isInvestor = roles.includes('investor');
        const isBacker = roles.includes('backer');
        
        // Determine primary role: admin > investor > backer > user
        let userRole = 'user';
        if (isAdmin) {
          userRole = 'admin';
        } else if (isInvestor) {
          userRole = 'investor';
        } else if (isBacker) {
          userRole = 'backer';
        }
        
        const user = {
          id: session.user.id,
          email: session.user.email,
          firstName: session.user.user_metadata?.first_name || '',
          lastName: session.user.user_metadata?.last_name || '',
          role: userRole,
          app_metadata: session.user.app_metadata, // Preserve app_metadata for role checks
          profile_photo: session.user.user_metadata?.profile_photo || null,
          createdAt: session.user.created_at,
          version: 4  // Version 4: Supports investor role in addition to admin
        };
        localStorage.setItem('defera.user', JSON.stringify(user));
        setCurrentUser(user);
        console.log('✅ User loaded:', user.email, 'Role:', user.role);
      } catch (error) {
        console.error('Auth initialization error:', error.message || error);
        // Clear everything on error
        localStorage.removeItem('defera.user');
        setCurrentUser(null);
      } finally {
        setIsLoadingUser(false);
      }
    };
    
    loadUser();
    
    // INDUSTRY STANDARD: Validate session immediately after load
    setTimeout(() => {
      if (currentUserRef.current) {
        validateSession();
      }
    }, 1000);
    
    // Subscribe to auth state changes - Supabase handles token refresh automatically
    const setupAuthListener = async () => {
      const { supabase } = await import('../lib/supabase');
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        // Ignore all events if we're redirecting
        if (isRedirectingRef.current) return;
        
        console.log('🔔 Auth state changed:', event);
        
        // IGNORE INITIAL_SESSION to prevent white screen flash on page load
        if (event === 'INITIAL_SESSION') {
          console.log('✅ Initial session loaded from storage - no action needed');
          return;
        }
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('✅ User signed in');
          
          // CRITICAL FIX: Load fresh user data from session instead of cache
          // This ensures admin/investor roles are properly detected immediately after login
          try {
            const roles = session.user.app_metadata?.roles || [];
            const isAdmin = roles.includes('admin');
            const isInvestor = roles.includes('investor');
            const isBacker = roles.includes('backer');
            
            // Determine primary role: admin > investor > backer > user
            let userRole = 'user';
            if (isAdmin) {
              userRole = 'admin';
            } else if (isInvestor) {
              userRole = 'investor';
            } else if (isBacker) {
              userRole = 'backer';
            }
            
            const freshUser = {
              id: session.user.id,
              email: session.user.email,
              firstName: session.user.user_metadata?.first_name || '',
              lastName: session.user.user_metadata?.last_name || '',
              role: userRole,
              app_metadata: session.user.app_metadata, // Preserve app_metadata for role checks
              profile_photo: session.user.user_metadata?.profile_photo || null,
              createdAt: session.user.created_at,
              version: 4  // Version 4: Supports investor role in addition to admin
            };
            
            // Update cache with fresh data FIRST
            localStorage.setItem('defera.user', JSON.stringify(freshUser));
            
            // Update state immediately - this triggers UI update for admin/investor tabs
            // Force a new object reference to ensure React detects the change
            setCurrentUser({ ...freshUser });
            setIsLoggingIn(false);
            setIsLoadingUser(false);
            
            console.log('✅ Fresh user data loaded after sign-in:', freshUser.email, 'Role:', freshUser.role);
            console.log('🎯 Admin/Investor/Backer tabs should now be visible:', userRole === 'admin' || userRole === 'investor' || userRole === 'backer');
            console.log('🔍 User roles from app_metadata:', roles);
          } catch (e) {
            console.error('Error loading fresh user data after sign-in:', e);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('✅ Token refreshed - session remains active');
          // No action needed - Supabase handles this automatically
        } else if (event === 'SIGNED_OUT') {
          console.log('🔴 User signed out - clearing session');
          // Only clear and redirect if we had a user logged in
          if (currentUserRef.current) {
            clearSessionAndRedirect();
          } else {
            // Already logged out, just ensure clean state
            setCurrentUser(null);
            localStorage.removeItem('defera.user');
          }
        } else if (event === 'USER_UPDATED') {
          console.log('🔄 User data updated');
          // Reload user data if there's a session
          if (session?.user) {
            // SECURITY FIX: Check app_metadata.roles (secure) instead of user_metadata.role (insecure)
            const roles = session.user.app_metadata?.roles || [];
            const isAdmin = roles.includes('admin');
            const isInvestor = roles.includes('investor');
            const isBacker = roles.includes('backer');
            
            // Determine primary role: admin > investor > backer > user
            let userRole = 'user';
            if (isAdmin) {
              userRole = 'admin';
            } else if (isInvestor) {
              userRole = 'investor';
            } else if (isBacker) {
              userRole = 'backer';
            }
            
            const user = {
              id: session.user.id,
              email: session.user.email,
              firstName: session.user.user_metadata?.first_name || '',
              lastName: session.user.user_metadata?.last_name || '',
              role: userRole,
              app_metadata: session.user.app_metadata, // Preserve app_metadata for role checks
              profile_photo: session.user.user_metadata?.profile_photo || null,
              createdAt: session.user.created_at,
              version: 4  // Version 4: Supports investor role in addition to admin
            };
            localStorage.setItem('defera.user', JSON.stringify(user));
            setCurrentUser(user);
          }
        } else if (event === 'USER_DELETED') {
          console.log('🔴 User account deleted');
          clearSessionAndRedirect();
        } else if (event === 'PASSWORD_RECOVERY') {
          console.log('🔑 Password recovery initiated');
          // No action needed - handled by password recovery flow
        }
      });
      
      // Store subscription reference
      authSubscription = subscription;
      
      return () => {
        subscription.unsubscribe();
      };
    };
    
    setupAuthListener();
    
    // INDUSTRY STANDARD: Periodic session validation (every 5 minutes)
    sessionCheckInterval = setInterval(() => {
      if (currentUserRef.current) {
        validateSession();
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    // INDUSTRY STANDARD: Validate session when user returns to tab
    visibilityHandler = () => {
      if (document.visibilityState === 'visible' && currentUserRef.current) {
        console.log('👁️ Tab became visible - validating session');
        validateSession();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    
    // Listen for API auth errors (session expired during use)
    const authErrorHandler = (event) => {
      console.warn('🚨 API auth error detected - logging out immediately');
      clearSessionAndRedirect();
    };
    window.addEventListener('auth-session-expired', authErrorHandler);
    
    // Cleanup subscription on unmount
    return () => {
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      window.removeEventListener('auth-session-expired', authErrorHandler);
    };
  }, []); // Run once on mount only - prevents infinite loops

  const handleSignOut = async () => {
    try {
      // iOS app: take this phone off the account while the session can still
      // prove whose it is (at most 3 seconds; nothing on the web).
      await import('@/native/push').then((m) => m.forgetThisPhone()).catch(() => {});

      // Clear UI state immediately
      setCurrentUser(null);
      
      // Try to sign out via backend, but don't wait for it
      UserEntity.logout().catch(err => {
        console.log('Backend logout failed (may not have valid session):', err);
      });
      
      // Clear all localStorage data
      localStorage.removeItem('defera.user');
      
      // Try to sign out from Supabase
      try {
        const { supabase } = await import('../lib/supabase');
        await supabase.auth.signOut();
      } catch (err) {
        console.log('Supabase sign out failed (may not have valid session):', err);
      }
      
      // Navigate to home page using React Router
      navigate(createPageUrl('Home'));
    } catch (error) {
      console.error('Error during sign out:', error);
      // Always clear and redirect, even on error
      setCurrentUser(null);
      localStorage.removeItem('defera.user');
      navigate(createPageUrl('Home'));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    // Trim + lowercase: a trailing space from mobile autocomplete is the most
    // common way a real address gets rejected. Supabase lowercases anyway;
    // doing it here keeps what we show the customer identical to what we sent.
    const email = resetEmail.trim().toLowerCase();
    if (!email || resetSending) return; // resetSending: a double-tap would trip the per-email rate limit

    setResetError(null);
    setResetSending(true);
    try {
      await UserEntity.resetPassword(email);
      setResetEmail(email);
      setResetEmailSent(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      // Never the raw Supabase message — it reads as something else entirely to
      // a customer (a "not found" that our stack cannot produce). Classified copy
      // that names the next step, plus a way out.
      setResetError(describeResetError(error));
    } finally {
      setResetSending(false);
    }
  };

  const getInitials = (first, last) => {
    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
    if (first) return first.substring(0, 2).toUpperCase();
    if (last) return last.substring(0, 2).toUpperCase();
    return 'U';
  };
  
  const isPublicBookingRoute = () => {
    const path = location.pathname.toLowerCase();
    return path.startsWith('/search') || path.startsWith('/flightoptions') || path.startsWith('/review') || path.startsWith('/explore') || path.startsWith('/rewards');
  };
  
  // Every gate the desktop top nav applies, in one place, so a tab folded into
  // another's dropdown (`desktopGroup`) is shown or hidden by exactly the rules
  // it would meet as a tab of its own.
  const canSeeInDesktopNav = (item) => {
    if (item.isBottomItem) return false;
    // Users and Activity live in the Workspace menu (and the mobile sheet),
    // keeping the main horizontal nav uncluttered.
    if (item.title === 'Users' || item.title === 'Activity') return false;
    if (item.requireReserveEligibility && !isReserveEligible) return false;
    const roles = currentUser?.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
    // Admins reach every admin page through the Workspace menu beside these
    // tabs; the inquiry desk keeps a tab of its own, as the page they open
    // all day. Investors keep their Analytics tab and the concierge desk its
    // Support tab: one page each needs no menu.
    if (isAdmin && (item.adminOnly || item.adminOrInvestor) && item.title !== 'Support') return false;
    if (item.adminOnly) {
      const allowed = isAdmin || (item.conciergeOk && roles.includes('concierge'));
      if (!currentUser || !allowed) return false;
    }
    if (item.adminOrInvestor) {
      const isInvestor = roles.includes('investor') || currentUser?.role === 'investor';
      // Analytics should only be visible to Admin or Investor (not Backers)
      if (!isAdmin && !isInvestor) return false;
    }
    return true;
  };

  // The admin pages as categories for the desktop Workspace menu (admins only).
  const workspaceCategories = (() => {
    const roles = currentUser?.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
    return isAdmin
      ? visibleAdminCategories({ isAdmin: true, isFounder: currentUser?.email === 'jayjay.gonzalez@icloud.com' })
      : [];
  })();

  // Case-insensitive: routes are registered as e.g. "/Accounting" while
  // createPageUrl links to "/accounting", and the router matches either.
  const isNavPathActive = (url) => !!url && location.pathname.toLowerCase() === url.toLowerCase();

  // This new structure ensures the background styles are always applied,
  // preventing the white flash during loading or navigation.
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        :root {
          --glass-bg: rgba(15, 15, 15, 0.6);
          --glass-light-bg: rgba(30, 30, 30, 0.5);
          --glass-border: rgba(255, 255, 255, 0.08);
          --glass-shadow: 0 16px 70px rgba(0, 0, 0, 0.5);
          --accent-color: hsl(228, 68%, 19%); /* Royal mid — #0F1952, same family as header */
          --accent-color-deep: hsl(228, 67%, 12%); /* Header wash anchor — #0A1233 */
          --text-primary: #f5f5f5;
          --text-secondary: #a3a3a3;
          --text-muted: #737373;
        }
        
        /* NOTE: this whole block is a JS template literal — never put a
           backtick in these comments, it ends the string and the CSS below
           gets parsed as arithmetic (renders the literal text "NaN", and
           node --check still passes because it is valid JS).

           Universal selector on purpose: only the button rule in index.css
           inherits font-family, so inputs/selects/textareas would drop to the
           browser default without this. Value comes from --font-sans in
           src/index.css so it cannot drift from the body rule again — this
           used to hardcode Inter while index.css declared an SF Pro stack,
           and because a universal selector beats inheritance, that SF stack
           never actually rendered.
           The Inter-only font-feature-settings (cv02/cv03/cv04/cv11) were
           dropped: they applied on Windows/Android and no-opped on Apple,
           pushing the two platforms further apart than necessary. */
        * {
          font-family: var(--font-sans);
        }
        
        body {
          background: #000;
          background-attachment: fixed;
          font-weight: 400;
          letter-spacing: -0.02em;
          color: var(--text-primary);
        }
        
        .glass {
          background: var(--glass-bg);
          backdrop-filter: blur(8px) saturate(120%);
          -webkit-backdrop-filter: blur(8px) saturate(120%);
          border: 1px solid var(--glass-border);
          box-shadow: var(--glass-shadow);
          will-change: transform;
        }

        .glass-light {
          background: var(--glass-light-bg);
          backdrop-filter: blur(8px) saturate(120%);
          -webkit-backdrop-filter: blur(8px) saturate(120%);
          border: 1px solid var(--glass-border);
          will-change: transform;
        }
        
        .gradient-bg {
          background: #000;
        }
        
        .hover-glow {
          transition: all 0.3s ease;
        }
        
        .hover-glow:hover {
          box-shadow: 0 0 30px rgba(10, 18, 51, 0.2), 0 0 10px rgba(10, 18, 51, 0.15);
          transform: translateY(-3px);
          border-color: rgba(10, 18, 51, 0.4);
        }
        
        .logo-pulse {
          box-shadow: 0 0 20px rgba(10, 18, 51, 0.3);
        }
      `}</style>
      
      {isLoadingUser ? (
        // Show loading state - prevents flash of home page
        <div className="min-h-[70vh] w-full bg-black" />
      ) : !currentUser && !isPublicBookingRoute() ? (
        <div className="min-h-screen w-full bg-brand-950 flex items-center justify-center py-12 px-6 relative overflow-y-auto">
          {/* Meta Pixel - Only loads on login page */}
          <MetaPixel />
          
          {/* Back to Index Button */}
          <div className="absolute top-6 left-6 z-50">
            <button
              onClick={() => navigate('/index')}
              className="text-white/30 hover:text-white/60 text-xs font-normal transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Back
            </button>
          </div>
          
          {/* Royal ambient background — the same world as the landing page,
              kept static (no animation) for Chrome performance. */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-brand-950 via-[#0a1024] to-[#060a18]" />
            <div
              className="absolute inset-0 opacity-70"
              style={{
                background: `
                  radial-gradient(circle 720px at 50% -5%, rgba(42, 56, 104, 0.38), transparent),
                  radial-gradient(circle 520px at 12% 28%, rgba(42, 56, 104, 0.22), transparent),
                  radial-gradient(circle 640px at 88% 38%, rgba(26, 35, 66, 0.30), transparent)
                `,
                filter: 'blur(40px)'
              }}
            />
            {/* Light horizon at the foot of the page — the landing's signature. */}
            <div
              className="absolute inset-x-0 bottom-0 h-72"
              style={{
                background: 'linear-gradient(to top, rgba(124, 139, 196, 0.16), rgba(124, 139, 196, 0.05) 45%, transparent)',
                filter: 'blur(28px)'
              }}
            />
          </div>
          
          <style>{`
            @media (prefers-reduced-motion: reduce) {
              * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
              }
            }
          `}</style>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="max-w-md w-full relative z-10"
          >
            <div className="text-center mb-8">
              <motion.img 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                src="/defera-logo-web.png"
                alt="Defera Logo" 
                className="h-14 mx-auto mb-6" 
              />
              <motion.h2 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-3xl font-light text-white mb-2"
              >
                Welcome to Defera
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-white/60 font-normal"
              >
                Sign in to search and book flights
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative rounded-3xl p-8 overflow-hidden bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 border border-brand-800/45 backdrop-blur-xl shadow-[0_24px_70px_-24px_rgba(4,7,18,0.9),inset_0_1px_0_0_rgba(255,255,255,0.08)]"
            >
              {/* Quiet top sheen so the card catches the ambient light. */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(150deg, rgba(255,255,255,0.06) 0%, transparent 42%)'
                }}
              />
              
              {/* Content */}
              <div className="relative z-10">
            
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                console.log('Form submitted - Email:', email, 'isSignup:', isSignup);
                
                if (!email || !password) {
                  console.log('Missing email or password');
                  return;
                }
                
                setIsLoggingIn(true);
                setAuthError('');
                console.log('Starting authentication...');
                
                try {
                  if (isSignup) {
                    if (!referralSource) {
                      setAuthError('Please select how you heard about us.');
                      setIsLoggingIn(false);
                      return;
                    }
                    console.log('Attempting signup...');
                    const user = await UserEntity.signup(email, password, {
                      firstName,
                      lastName,
                      referral_source: referralSource
                    });
                    console.log('Signup successful - user automatically logged in');

                    // ChatGPT Ads conversion: the account now exists. Fired here
                    // rather than on the redirect that follows, because a customer
                    // who signs up and immediately closes the tab has still
                    // converted. Never throws — see src/lib/chatgptAdsPixel.js.
                    trackSignupConversion(user);
                    
                    // Show success message - user is automatically logged in now
                    toast.success('Account created successfully!', {
                      duration: 5000,
                      style: {
                        background: '#10b981',
                        color: 'white',
                        border: '1px solid #059669'
                      }
                    });
                    
                    // Set user state - user is automatically authenticated after signup
                    setCurrentUser(user);
                    setEmail('');
                    setPassword('');
                    setFirstName('');
                    setLastName('');
                    setReferralSource('');
                    setIsLoggingIn(false);
                    
                    console.log('🎯 User state updated after signup - UI will render with user:', user.email);
                    
                    // Check for saved redirect path from AuthGate
                    const redirectPath = sessionStorage.getItem('auth_redirect');
                    if (redirectPath) {
                      console.log('📍 Redirecting to saved path after signup:', redirectPath);
                      sessionStorage.removeItem('auth_redirect');
                      navigate(redirectPath);
                    }
                  } else {
                    console.log('Attempting login...');
                    const user = await UserEntity.login(email, password);
                    console.log('✅ Login successful! User:', user.email, 'Role:', user.role);
                    
                    // Set user state - this will automatically hide login screen and show app
                    setCurrentUser(user);
                    setEmail('');
                    setPassword('');
                    setIsLoggingIn(false);
                    
                    console.log('🎯 User state updated - UI will render with admin tabs:', user.role === 'admin');
                    
                    // Check for saved redirect path from AuthGate
                    const redirectPath = sessionStorage.getItem('auth_redirect');
                    if (redirectPath) {
                      console.log('📍 Redirecting to saved path after login:', redirectPath);
                      sessionStorage.removeItem('auth_redirect');
                      navigate(redirectPath);
                    }
                  }
                } catch (error) {
                  console.error('Authentication failed:', error);
                  console.error('Error message:', error.message);
                  console.error('Error details:', JSON.stringify(error, null, 2));
                  setAuthError(error.message || 'Authentication failed. Please try again.');
                  setIsLoggingIn(false); // Only reset on error
                }
                // Note: isLoggingIn will be reset by auth state change on success
              }}
              className="space-y-5"
            >
              {isSignup && (
                <div className="text-center -my-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignup(!isSignup);
                      setAuthError('');
                    }}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm text-white/60 hover:text-white hover:bg-white/5 border border-white/10 transition-colors font-normal"
                  >
                    Already have an account? Sign in
                  </button>
                </div>
              )}
              
              {isSignup && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="firstName" className="text-white/55 text-[13px] font-normal">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      required
                      className="bg-white/[0.04] border-white/10 text-white placeholder-white/30 hover:bg-white/[0.06] hover:border-white/20 focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 h-10 rounded-full px-5 font-normal transition-all"
                    />
                  </motion.div>
                  
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="lastName" className="text-white/55 text-[13px] font-normal">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      required
                      className="bg-white/[0.04] border-white/10 text-white placeholder-white/30 hover:bg-white/[0.06] hover:border-white/20 focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 h-10 rounded-full px-5 font-normal transition-all"
                    />
                  </motion.div>
                  </div>

                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="referralSource" className="text-white/55 text-[13px] font-normal">
                      How did you hear about us?
                    </Label>
                    <Select value={referralSource} onValueChange={setReferralSource} required>
                      <SelectTrigger className="bg-white/[0.04] border-white/10 text-white hover:bg-white/[0.06] hover:border-white/20 h-10 rounded-full px-5 font-normal">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                        <SelectContent className="rounded-xl bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white">
                        {REFERRAL_SOURCE_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value} className="text-white hover:bg-white/10">{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </motion.div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/55 text-[13px] font-normal">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="bg-white/[0.04] border-white/10 text-white placeholder-white/30 hover:bg-white/[0.06] hover:border-white/20 focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 h-10 rounded-full px-5 font-normal transition-all"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-white/55 text-[13px] font-normal">
                    Password
                  </Label>
                  {!isSignup && (
                    <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-sm text-primary hover:text-primary/80 transition-colors font-normal"
                        >
                          Forgot password?
                        </button>
                      </DialogTrigger>
                      <DialogContent className="rounded-2xl bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white">
                        <DialogHeader>
                          <DialogTitle className="text-white font-light text-xl">Reset Password</DialogTitle>
                          <DialogDescription className="text-white/60 font-normal">
                            {resetEmailSent
                              ? `If there's an account for ${resetEmail}, a reset link is on its way.`
                              : "Enter your email address and we'll send you a link to reset your password."
                            }
                          </DialogDescription>
                        </DialogHeader>
                        {!resetEmailSent ? (
                          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor="reset-email" className="text-white/80 font-normal">Email</Label>
                              <Input
                                id="reset-email"
                                type="email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                className="bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[var(--accent-color)] focus:ring-1 focus:ring-[var(--accent-color)] h-12 rounded-xl font-normal"
                              />
                            </div>
                            {resetError && (
                              <div className="text-sm bg-red-500/10 border border-red-500/20 rounded-xl p-3 font-normal space-y-1" role="alert">
                                <p className="text-red-300 font-medium">{resetError.title}</p>
                                <p className="text-white/75">{resetError.detail}</p>
                                <p className="text-white/50 text-xs pt-1">{RESET_SUPPORT_LINE}</p>
                              </div>
                            )}
                            <Button type="submit" disabled={resetSending} className={`${BRAND_PRIMARY_BTN_CLASS} w-full h-12 rounded-full disabled:opacity-60`}>
                              {resetSending ? 'Sending…' : 'Send Reset Link'}
                            </Button>
                          </form>
                        ) : (
                          <div className="mt-4 space-y-4">
                            {/* What to expect, so a slow or filtered email doesn't read as a failure —
                                and doesn't send anyone back to the sign-up form to "try again". */}
                            <ul className="text-sm text-white/70 font-normal space-y-1.5 list-disc pl-5">
                              <li>It usually arrives within a couple of minutes. Check <span className="text-white/90">Spam</span> or <span className="text-white/90">Promotions</span> if it isn&apos;t in your inbox.</li>
                              <li>The link works once. If it&apos;s expired by the time you open it, just request another.</li>
                              <li>Didn&apos;t get it after a few minutes? Try again — or <Link to="/contact#message" className="text-white/90 underline">send us a message</Link> and we&apos;ll send one directly.</li>
                            </ul>
                            <Button
                              onClick={() => {
                                setShowForgotPassword(false);
                                setResetEmailSent(false);
                                setResetEmail('');
                                setResetError(null);
                              }}
                              className={`${BRAND_PRIMARY_BTN_CLASS} w-full h-12 rounded-full`}
                            >
                              Close
                            </Button>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-white/[0.04] border-white/10 text-white placeholder-white/30 hover:bg-white/[0.06] hover:border-white/20 focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 h-10 rounded-full px-5 font-normal transition-all"
                />
              </div>

              
              {authError && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl p-4 font-normal"
                >
                  {authError}
                </motion.div>
              )}
              
              <Button
                type="submit"
                disabled={!email || !password || isLoggingIn || (isSignup && (!firstName || !lastName || !referralSource))}
                className={`${BRAND_PRIMARY_BTN_CLASS} w-full h-12 rounded-full text-base tracking-wide disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoggingIn ? (isSignup ? 'Creating Account...' : 'Signing In...') : (isSignup ? 'Create Account' : 'Sign In')}
              </Button>
              
              {!isSignup && (
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignup(!isSignup);
                      setAuthError('');
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors font-normal"
                  >
                    Don't have an account? Sign up
                  </button>
                </div>
              )}
            </form>
            </div>
            </motion.div>
          </motion.div>
        </div>
      ) : (
        <div className="min-h-[70vh] w-full gradient-bg font-sans relative">
          {!hideNav && (
          <header className="fixed top-0 left-0 right-0 z-50 p-2">
            {/* Three changes here, all invisible on a working browser, all
                aimed at the same bug: in the installed PWA on an iPhone 18 Pro
                Max the top of this pill's border fades out while the bottom
                stays crisp. An iPhone 14 on the same build is fine, and a
                Safari tab on the same phone is fine — so it is WebKit's
                backdrop compositing, not our CSS, which is symmetric on all
                four sides and identical on every device.

                1. No `will-change: transform`. It was the only use of it in the
                   codebase and the nav never animates, so it bought nothing but
                   a permanently promoted compositing layer — and a promoted
                   layer samples its backdrop from its own snapshot.
                2. `backdrop-blur-sm` (4px) on mobile instead of `-md` (12px).
                   The pill sits 8px below the viewport top (header p-2), so a
                   12px blur reaches past the backdrop root and comes back
                   empty across the flat top edge, which is exactly where the
                   fade is worst while the corners stay sharp. 4px fits inside
                   the gap. Over a near-black backdrop the two are
                   indistinguishable; desktop keeps its 24px blur.
                3. The hairline is an inset ring rather than a real border.
                   A border on a backdrop-filtered element is drawn against the
                   filtered region and gets clipped with it; an inset shadow is
                   painted by the element itself and cannot be.

                Shipped together on purpose: the bug does not reproduce in
                Chromium or on any device here, so one deploy that removes all
                three candidates is worth more than three round trips. */}
            <div className="bg-black/40 backdrop-blur-sm md:backdrop-blur-xl rounded-full p-3 md:p-2 flex items-center justify-between max-w-6xl mx-auto shadow-[0_8px_32px_0_rgba(0,0,0,0.37),inset_0_0_0_1px_rgba(255,255,255,0.2)]">
              <Link to={currentUser ? createPageUrl('Home') : '/'} className="flex items-center gap-3 group">
                <img src="/defera-logo-web.png" alt="Defera Logo" className="px-2 h-7 transition-transform duration-300 group-hover:scale-105" />
              </Link>
  
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-2">
                  {navigationItems.map((item) => {
                    if (!canSeeInDesktopNav(item)) return null;
                    // Rendered inside its group's dropdown instead.
                    if (item.desktopGroup) return null;

                    // A tab others fold into becomes a dropdown: itself first,
                    // then each member the viewer may see. With no visible
                    // members it stays a plain tab.
                    const groupMembers = navigationItems.filter(
                      (other) => other.desktopGroup === item.title && canSeeInDesktopNav(other),
                    );
                    if (groupMembers.length > 0) {
                      const entries = [item, ...groupMembers];
                      const groupActive = entries.some((entry) => isNavPathActive(entry.url));
                      return (
                        <DropdownMenu key={item.title}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className={`text-sm transition-all duration-300 px-4 py-1.5 h-auto rounded-full flex items-center gap-1.5 ${groupActive ? 'glass-light' : 'hover:bg-white/10 text-gray-300 hover:text-white'}`}>
                              {item.title}
                              <ChevronDown className={`w-3 h-3 ${groupActive ? 'text-white/60' : 'text-gray-500'}`} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="glass-light text-white border-white/10" align="start">
                            {entries.map((entry) => (
                              <DropdownMenuItem key={entry.title} asChild>
                                <Link to={entry.url} className={`flex items-center cursor-pointer w-full ${isNavPathActive(entry.url) ? 'bg-white/10' : ''}`}>
                                  <entry.icon className="w-4 h-4 mr-2" />
                                  {entry.title}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    }

                    if (item.isDropdown) {
                      return (
                        <DropdownMenu key={item.title}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="text-sm transition-all duration-300 px-4 py-1.5 h-auto rounded-full hover:bg-white/10 text-gray-300 hover:text-white flex items-center gap-1.5">
                              <item.icon className="w-4 h-4 hidden" />
                              {item.title}
                              <ChevronDown className="w-3 h-3 text-gray-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="glass-light text-white border-white/10" align="end">
                            {item.subItems.map((subItem) => (
                              <DropdownMenuItem key={subItem.title} asChild>
                                <Link to={subItem.url} className="flex items-center cursor-pointer w-full">
                                  <subItem.icon className="w-4 h-4 mr-2" />
                                  {subItem.title}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    }
  
                    // Conditionally set Home URL based on login status
                    const homeUrl = item.title === 'Home' 
                      ? (currentUser ? createPageUrl('Home') : '/')
                      : item.url;
                    
                    return (
                      <Button key={item.title} asChild variant="ghost" className={`text-sm transition-all duration-300 px-4 py-1.5 h-auto rounded-full ${location.pathname === item.url ? 'glass-light' : 'hover:bg-white/10 text-gray-300 hover:text-white'}`}>
                        <Link to={homeUrl} className="flex items-center">
                          <item.icon className="w-4 h-4 mr-2 hidden" />
                          {item.title}
                        </Link>
                      </Button>
                    );
                  })}
                  {workspaceCategories.length > 0 && (
                    <AdminNavMegaMenu categories={workspaceCategories} pathname={location.pathname} />
                  )}
              </nav>
  
              <div className="flex items-center">
                {/* Desktop User Menu */}
                <div className="hidden md:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-8 w-8 rounded-full hover:bg-white/5 p-0">
                        <Menu className="h-6 w-6 text-white/80" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      sideOffset={10}
                      className="mr-4 w-60 p-0 overflow-hidden rounded-2xl border-white/10 bg-black/90 backdrop-blur-2xl text-white shadow-[0_24px_64px_-16px_rgba(0,0,0,0.9)]"
                    >
                      {(() => {
                        const roles = currentUser?.app_metadata?.roles || [];
                        const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
                        const isInvestor = roles.includes('investor') || currentUser?.role === 'investor';
                        const isBacker = roles.includes('backer') || currentUser?.role === 'backer';

                        const fullName = currentUser?.firstName && currentUser?.lastName
                          ? `${currentUser.firstName} ${currentUser.lastName}`
                          : 'My Account';

                        // Radix marks the highlighted row via focus, so focus:*
                        // styles cover both pointer hover and keyboard navigation.
                        const ITEM_CLASS = 'group flex items-center gap-2.5 cursor-pointer rounded-lg px-2.5 py-2 text-[13px] font-normal text-white/85 focus:bg-white/[0.07] focus:text-white transition-colors';
                        const ICON_CLASS = 'w-4 h-4 flex-shrink-0 text-white/40 group-focus:text-[hsl(228,35%,58%)] transition-colors';
                        const EYEBROW_CLASS = 'px-2.5 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.22em] text-white/30 font-normal';

                        const accountItems = [
                          { title: 'Profile', url: createPageUrl('Profile'), icon: UserCircle },
                          { title: 'How it Works', url: '/how-it-works', icon: BookOpen },
                          ...(isAdmin || isInvestor || isBacker
                            ? [{ title: 'Investor', url: createPageUrl('Investor'), icon: TrendingUp }]
                            : []),
                        ];

                        // Every admin page (Activity, Users, Settings and the
                        // rest) lives in the Workspace menu in the top bar.
                        const supportItems = [
                          { title: 'Support', url: '/support', icon: Sparkles },
                          { title: 'Help Center', url: '/help', icon: HelpCircle },
                        ];

                        const renderItems = (items) => items.map((item) => (
                          <DropdownMenuItem key={item.title} asChild className={ITEM_CLASS}>
                            <Link to={item.url}>
                              <item.icon className={ICON_CLASS} />
                              {item.title}
                            </Link>
                          </DropdownMenuItem>
                        ));

                        return (
                          <>
                            {/* Identity header */}
                            <div className="px-4 py-3.5 border-b border-white/[0.08] bg-white/[0.02]">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <p className="text-white text-[13px] font-normal leading-tight truncate">{fullName}</p>
                                <MyVipBadge />
                              </div>
                              {currentUser?.email && (
                                <p className="text-white/35 text-[11px] font-normal leading-tight truncate mt-0.5">{currentUser.email}</p>
                              )}
                            </div>

                            {/* Scrollable body — admin accounts carry a lot of
                                entries, so cap the height and let it scroll
                                instead of running off-screen. */}
                            <div className="max-h-[min(60vh,520px)] overflow-y-auto p-1.5">
                              <DropdownMenuLabel className={EYEBROW_CLASS}>Account</DropdownMenuLabel>
                              {renderItems(accountItems)}

                              <DropdownMenuSeparator className="bg-white/[0.06] my-1.5" />
                              <DropdownMenuLabel className={EYEBROW_CLASS}>Support</DropdownMenuLabel>
                              {renderItems(supportItems)}
                              {showInstallButton && (
                                <DropdownMenuItem onClick={handleInstallClick} className={ITEM_CLASS}>
                                  <Download className={ICON_CLASS} />
                                  Download App
                                </DropdownMenuItem>
                              )}
                            </div>

                            {/* Sign out — pinned below the scroll area */}
                            <div className="p-1.5 border-t border-white/[0.08]">
                              <DropdownMenuItem
                                onClick={handleSignOut}
                                className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2.5 py-2 text-[13px] font-normal text-red-400/90 focus:bg-red-500/10 focus:text-red-300 transition-colors"
                              >
                                <LogOut className="w-4 h-4 flex-shrink-0" />
                                Sign Out
                              </DropdownMenuItem>
                            </div>
                          </>
                        );
                      })()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
  
                {/* Mobile Hamburger Menu — mirrors the desktop user dropdown
                    with an identity header, sectioned eyebrow labels, compact
                    rows, and a pinned Sign Out. */}
                <div className="md:hidden">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon" className="hover:bg-white/5 rounded-full w-10 h-10" style={{ color: 'var(--text-secondary)' }}>
                        <Menu className="w-10 h-10" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="right"
                      className="p-0 w-80 sm:max-w-sm border-l border-white/10 bg-brand-950/95 backdrop-blur-2xl text-white flex flex-col gap-0 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.9)]"
                    >
                      {(() => {
                        const roles = currentUser?.app_metadata?.roles || [];
                        const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
                        const isInvestor = roles.includes('investor') || currentUser?.role === 'investor';
                        const isBacker = roles.includes('backer') || currentUser?.role === 'backer';
                        const isConcierge = roles.includes('concierge');
                        const isFounder = currentUser?.email === 'jayjay.gonzalez@icloud.com';
                        // The admin pages as categories (src/constants/adminNavCategories.js):
                        // admins see every area, the concierge desk role only
                        // Inquiries, investors only Analytics.
                        const adminCategories = visibleAdminCategories({ isAdmin, isInvestor, isConcierge, isFounder });

                        const fullName = currentUser?.firstName && currentUser?.lastName
                          ? `${currentUser.firstName} ${currentUser.lastName}`
                          : 'Traveler';

                        // Match the desktop dropdown’s row/eyebrow classes so
                        // the two menus feel like the same component. Padding
                        // is bumped slightly for comfortable touch targets.
                        // text-base (not the desktop dropdown's text-sm) —
                        // matches the original mobile sheet's font size.
                        const ITEM_CLASS = 'group flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2.5 text-base font-normal text-white/90 hover:bg-white/[0.07] hover:text-white transition-colors w-full';
                        const ICON_CLASS = 'w-4 h-4 flex-shrink-0 text-white/40 group-hover:text-[hsl(228,35%,58%)] transition-colors';
                        const EYEBROW_CLASS = 'px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-white/30 font-normal';
                        const SEPARATOR_CLASS = 'h-px bg-white/[0.06] my-1.5 mx-1';

                        // Primary navigation — mobile-only section since the
                        // horizontal top nav is hidden below md. Filters
                        // mirror the desktop nav-bar loop above.
                        const navSection = navigationItems.filter((item) => {
                          if (item.isBottomItem) return false;
                          // Every admin page (the Support desk included) lives
                          // in the Workspace categories below, gated there.
                          if (item.adminOnly || item.adminOrInvestor) return false;
                          if (item.requireReserveEligibility && !isReserveEligible) return false;
                          return true;
                        });

                        const accountItems = [
                          { title: 'Profile', url: createPageUrl('Profile'), icon: UserCircle },
                          { title: 'Passengers', url: createPageUrl('Profile'), icon: UserCog },
                          { title: 'How it Works', url: '/how-it-works', icon: BookOpen },
                          ...(isAdmin || isInvestor || isBacker
                            ? [{ title: 'Investor', url: createPageUrl('Investor'), icon: TrendingUp }]
                            : []),
                        ];

                        const supportItems = [
                          { title: 'Contact Us', url: createPageUrl('contact'), icon: MessageCircle },
                          { title: 'Support', url: '/support', icon: Sparkles },
                          { title: 'Help Center', url: '/help', icon: HelpCircle },
                        ];

                        const renderItems = (items) => items.map((item) => {
                          // Home should route to the marketing landing when
                          // the user is signed out (mirrors the desktop nav).
                          const targetUrl = item.title === 'Home'
                            ? (currentUser ? createPageUrl('Home') : '/')
                            : item.url;
                          return (
                            <SheetClose asChild key={item.title}>
                              <Link to={targetUrl} className={ITEM_CLASS}>
                                <item.icon className={ICON_CLASS} />
                                <span>{item.title}</span>
                              </Link>
                            </SheetClose>
                          );
                        });

                        return (
                          <>
                            {/* Identity header */}
                            <div className="px-5 py-4 border-b border-white/[0.08] bg-white/[0.02] shrink-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <p className="text-white text-sm font-normal leading-tight truncate">{fullName}</p>
                                <MyVipBadge />
                              </div>
                              {currentUser?.email && (
                                <p className="text-white/35 text-xs font-normal leading-tight truncate mt-1">{currentUser.email}</p>
                              )}
                            </div>

                            {/* Scrollable body */}
                            <div className="flex-1 overflow-y-auto p-2">
                              {navSection.length > 0 && (
                                <>
                                  <p className={EYEBROW_CLASS}>Navigation</p>
                                  {renderItems(navSection)}
                                </>
                              )}

                              {/* Account is the customer-shaped group admins open
                                  the drawer for least, so for them it sits at
                                  the bottom; customers keep it right up top. */}
                              {!isAdmin && (
                                <>
                                  {navSection.length > 0 && <div className={SEPARATOR_CLASS} />}
                                  <p className={EYEBROW_CLASS}>Account</p>
                                  {renderItems(accountItems)}
                                </>
                              )}

                              {/* The admin pages as categories with their pages
                                  inside: a console menu, not twenty-odd rows.
                                  Search only when there's enough to search. */}
                              {adminCategories.length > 0 && (
                                <>
                                  <div className={SEPARATOR_CLASS} />
                                  <p className={EYEBROW_CLASS}>Workspace</p>
                                  <AdminNavCategories
                                    categories={adminCategories}
                                    pathname={location.pathname}
                                    showSearch={isAdmin}
                                    wrapItem={(item, link) => <SheetClose asChild>{link}</SheetClose>}
                                  />
                                </>
                              )}

                              <div className={SEPARATOR_CLASS} />
                              <p className={EYEBROW_CLASS}>Support</p>
                              {renderItems(supportItems)}
                              {showInstallButton && (
                                <SheetClose asChild>
                                  <button
                                    type="button"
                                    onClick={handleInstallClick}
                                    className={`${ITEM_CLASS} text-left`}
                                  >
                                    <Download className={ICON_CLASS} />
                                    <span>Download App</span>
                                  </button>
                                </SheetClose>
                              )}

                              {/* Admin-only placement — see the note above the
                                  customer-position Account block. */}
                              {isAdmin && (
                                <>
                                  <div className={SEPARATOR_CLASS} />
                                  <p className={EYEBROW_CLASS}>Account</p>
                                  {renderItems(accountItems)}
                                </>
                              )}
                            </div>

                            {/* Sign out — pinned below the scroll area */}
                            <div className="p-2 border-t border-white/[0.08] shrink-0">
                              <SheetClose asChild>
                                <button
                                  type="button"
                                  onClick={handleSignOut}
                                  className="flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2.5 text-base font-normal text-red-400/90 hover:bg-red-500/10 hover:text-red-300 transition-colors w-full text-left"
                                >
                                  <LogOut className="w-4 h-4 flex-shrink-0" />
                                  <span>Sign Out</span>
                                </button>
                              </SheetClose>
                            </div>
                          </>
                        );
                      })()}
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            </div>
          </header>
          )}

          <main className="relative z-10 app-main-content">
            {children}
          </main>
        </div>
      )}
    </>
  );
}

